import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, X, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function CoHostSection({ event, onChanged }) {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const isOwner = user?.id === event.owner_id;

  const add = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post(`/events/${event.id}/co-hosts`, { email: email.trim() });
      toast.success("Co-host added");
      setEmail("");
      onChanged && onChanged();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const remove = async (coHostId) => {
    if (!window.confirm("Remove this co-host?")) return;
    try {
      await api.delete(`/events/${event.id}/co-hosts/${coHostId}`);
      toast.success("Removed");
      onChanged && onChanged();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const coHostRows = (event.co_hosts || []).map((cid, i) => ({ id: cid, email: (event.co_host_emails || [])[i] }));

  return (
    <div data-testid="co-hosts-section" className="border border-black/10 bg-white p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-neutral-500">
            <Users size={12} /> Co-hosts
          </div>
          <p className="text-sm text-neutral-600 mt-2 max-w-md">
            Co-hosts can view the invite, see RSVPs, and publish. Only the primary host can delete the event.
          </p>
        </div>
      </div>

      {coHostRows.length > 0 && (
        <div className="mt-4 divide-y divide-black/5 border-t border-black/5">
          {coHostRows.map((c) => (
            <div key={c.id} data-testid={`co-host-${c.id}`} className="py-3 flex items-center justify-between">
              <div className="text-sm">{c.email || c.id}</div>
              {isOwner && (
                <button onClick={() => remove(c.id)} data-testid={`co-host-remove-${c.id}`} className="text-neutral-400 hover:text-red-600">
                  <X size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {isOwner && (
        <div className="mt-4 flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-[10px] tracking-[0.25em] uppercase">Invite by email</Label>
            <Input
              data-testid="co-host-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="friend@example.com"
              className="rounded-sm mt-1"
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
          </div>
          <Button onClick={add} disabled={loading} data-testid="co-host-add" className="rounded-sm bg-neutral-900 hover:bg-neutral-800">
            <UserPlus size={14} className="mr-2" /> Add
          </Button>
        </div>
      )}
    </div>
  );
}
